import type { PostHogInitialUtmProperties } from "@shared/constants";
import {
  parsePostHogCrossDomainHash,
  stripPostHogCrossDomainHash,
} from "@shared/posthog/crossDomainHash";

import { readAnalyticsConsentCookie } from "./analyticsConsentCookie";

export const POSTHOG_SIGNUP_ATTRIBUTION_SESSION_KEY = "asr_posthog_signup_attribution";

export interface StoredPostHogSignupAttribution {
  marketingDistinctId: string;
  sessionId: string | null;
  signupSource: string;
  host: string;
  initialUtms: PostHogInitialUtmProperties | null;
  storedAt: string;
}

const UTM_PROPERTY_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

let bootstrapConsumed = false;

function readHashFromLocation(): string {
  if (typeof window === "undefined") return "";
  return window.location.hash;
}

function replaceLocationHash(nextHash: string): void {
  if (typeof window === "undefined") return;
  const { pathname, search } = window.location;
  const url = nextHash ? `${pathname}${search}${nextHash}` : `${pathname}${search}`;
  window.history.replaceState(window.history.state, "", url);
}

/**
 * Reads PostHog cross-domain hash params once, strips them from the URL, and caches attribution
 * for the registration flow. Only runs when analytics consent is already granted in the shared cookie.
 */
export function consumePostHogCrossDomainBootstrap(): {
  distinctID: string;
  sessionID?: string;
} | null {
  if (bootstrapConsumed || typeof window === "undefined") {
    return readStoredBootstrapForInit();
  }

  bootstrapConsumed = true;

  const consent = readAnalyticsConsentCookie();
  const hash = readHashFromLocation();
  const { distinctId, sessionId } = parsePostHogCrossDomainHash(hash);

  if (distinctId || sessionId) {
    const stripped = stripPostHogCrossDomainHash(hash);
    replaceLocationHash(stripped);
  }

  if (consent?.choice !== "granted" || !distinctId) {
    return null;
  }

  const attribution: StoredPostHogSignupAttribution = {
    marketingDistinctId: distinctId,
    sessionId: sessionId ?? null,
    signupSource: "www_register_link",
    host: window.location.hostname,
    initialUtms: null,
    storedAt: new Date().toISOString(),
  };
  sessionStorage.setItem(POSTHOG_SIGNUP_ATTRIBUTION_SESSION_KEY, JSON.stringify(attribution));

  return {
    distinctID: distinctId,
    ...(sessionId ? { sessionID: sessionId } : {}),
  };
}

function readStoredBootstrapForInit(): {
  distinctID: string;
  sessionID?: string;
} | null {
  if (typeof window === "undefined") return null;
  const raw = sessionStorage.getItem(POSTHOG_SIGNUP_ATTRIBUTION_SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredPostHogSignupAttribution;
    if (!parsed.marketingDistinctId) return null;
    return {
      distinctID: parsed.marketingDistinctId,
      ...(parsed.sessionId ? { sessionID: parsed.sessionId } : {}),
    };
  } catch {
    return null;
  }
}

export function readStoredPostHogSignupAttribution(): StoredPostHogSignupAttribution | null {
  if (typeof window === "undefined") return null;
  const raw = sessionStorage.getItem(POSTHOG_SIGNUP_ATTRIBUTION_SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredPostHogSignupAttribution;
    if (!parsed.marketingDistinctId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function mergeInitialUtmsFromPostHog(
  attribution: StoredPostHogSignupAttribution,
  getProperty: (key: string) => unknown
): StoredPostHogSignupAttribution {
  const initialUtms: PostHogInitialUtmProperties = {};
  let hasAny = false;
  for (const key of UTM_PROPERTY_KEYS) {
    const initialKey = `$initial_${key}`;
    const value = getProperty(initialKey);
    if (typeof value === "string" && value.trim()) {
      initialUtms[key] = value;
      hasAny = true;
    }
  }
  if (!hasAny) {
    return attribution;
  }
  const next = { ...attribution, initialUtms };
  sessionStorage.setItem(POSTHOG_SIGNUP_ATTRIBUTION_SESSION_KEY, JSON.stringify(next));
  return next;
}

export function clearStoredPostHogSignupAttribution(): void {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(POSTHOG_SIGNUP_ATTRIBUTION_SESSION_KEY);
}
